import { forwardRef, type PropsWithChildren, useRef } from 'react';


import { ChartGridArea } from './ChartGridArea.js';
import { ChartGridPlot } from './ChartGridPlot.js';
import { GridPlotSizeProvider } from './providers/GridPlotSizeProvider.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import { GridProps, Grid } from '../../../../layouts/grid/Grid.js';

/**
 * @internal
 */
export interface ChartGridProps {
  width: GridProps<'div'>['width'];
  height: GridProps<'div'>['height'];
  gridTemplateAreas: string;
  gridTemplateColumns: string;
  gridTemplateRows: string;
}

/**
 *
 * @internal
 * The layout used to have grid on a chart
 */
export const ChartGridRoot = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  PropsWithChildren<ChartGridProps>
>(
  (
    {
      width,
      height,
      gridTemplateAreas,
      gridTemplateColumns,
      gridTemplateRows,
      children,
      ...props
    },
    forwardedRef,
  ) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergeRefs([forwardedRef, chartRef]);

    return (
      <Grid
        ref={mergedRef}
        width={width}
        height={height}
        gridTemplateAreas={gridTemplateAreas}
        gridTemplateColumns={gridTemplateColumns}
        gridTemplateRows={gridTemplateRows}
        minHeight="min-content"
        gap={0}
        {...props}
      >
        <GridPlotSizeProvider>{children}</GridPlotSizeProvider>
      </Grid>
    );
  },
);
(ChartGridRoot as typeof ChartGridRoot & { displayName: string }).displayName =
  'ChartGridRoot';

/** @internal */
export const ChartGrid = Object.assign(ChartGridRoot, {
  PlotArea: ChartGridPlot,
  GridArea: ChartGridArea,
});
